import {
  allPass,
  any,
  compose,
  contains,
  filter,
  find,
  flatten,
  head,
  intersection,
  isEmpty,
  map,
  pluck,
  prop,
  propEq,
  uniq,
} from "ramda";
import { useMemo } from "react";
import { TEMPORARY_GLOBAL_NAV_ITEMS } from "components/chrome/sidebar/navigation_items_provider";
import { findSecondaryNavItem } from "helpers/chrome/navigation";
import { useFeatureRouteParams } from "helpers/shared/hooks/useFeatureRouteParams";
import {
  type IBrandGroup,
  pluckEntityStores,
} from "helpers/transforms/entities";
import type { IEntity, IEntityApp } from "interfaces/app";

const getAppBase = (vendorLevel: string, vendorLocation: string): string => {
  return `/${vendorLevel}/${vendorLocation}`;
};

const excludeAppBaseFromPath = (
  pathname: string,
  vendorLevel: string,
  vendorLocation: string
): string => {
  const appBaseLength = getAppBase(vendorLevel, vendorLocation).length;
  return pathname.slice(appBaseLength);
};

const passPresentURLParams = (
  keyWhitelist: string[],
  params: URLSearchParams
) => {
  const forwardedParams = keyWhitelist.reduce((acc, key) => {
    if (params.has(key)) {
      const val = params.get(key) || "";
      return acc.concat(
        `${encodeURIComponent(key)}=${encodeURIComponent(val)}`
      );
    }
    return acc;
  }, [] as string[]);
  if (forwardedParams.length) {
    return `?${forwardedParams.join("&")}`;
  }
  return "";
};

const sameBaseDifferentPath = (currPath: string, newPath: string): string => {
  const base = /^\/[b|s]\/[^\/]*\//.exec(currPath);
  if (newPath.startsWith("/")) {
    newPath = newPath.slice(1);
  }
  return base ? base[0] + newPath : "";
};

const samePathDifferentStore = (
  pathname: string,
  currentLevel: string,
  currentLocationId: string,
  newStoreId: string
): string => {
  return `${getAppBase("s", newStoreId)}${excludeAppBaseFromPath(
    pathname,
    currentLevel,
    currentLocationId
  )}`;
};

const samePathDifferentBrand = (
  pathname: string,
  currentLevel: string,
  currentLocationId: string,
  newBrandId: string
): string => {
  return `${getAppBase("b", newBrandId)}${excludeAppBaseFromPath(
    pathname,
    currentLevel,
    currentLocationId
  )}`;
};

// Helpers for getRedirect.
const APPS = ["swipely", "breadcrumb", "inventory"];

const storeHasApps = (store: IEntity): boolean => {
  return !isEmpty(
    intersection(
      APPS,
      map((app) => app.name, store.apps)
    )
  );
};

const brandHasApps = (brand: IBrandGroup): boolean => {
  return any(storeHasApps, brand.stores);
};

const appsForStores = (stores: IEntity[]): string[] => {
  return compose(
    uniq as (list: string[]) => string[],
    pluck("name") as (apps: IEntityApp[]) => string[],
    flatten as (storeApps: IEntityApp[][]) => IEntityApp[],
    pluck("apps") as (stores: IEntity[]) => IEntityApp[][]
  )(stores);
};

const storeIdForApp = (app: string, stores: IEntity[]): string | undefined => {
  const store: IEntity | undefined = find(
    compose(
      contains<string>(app),
      pluck("name") as (app: IEntityApp[]) => string[],
      prop("apps")
    ),
    stores
  );
  return store && store.id;
};

const getRedirect = (
  brands: IBrandGroup[],
  vendorLevel: string = "s",
  vendorLocation?: string,
  locationName?: string
): string => {
  switch (vendorLevel) {
    case "b": {
      let brand = find(
        allPass([propEq("brandId", vendorLocation), brandHasApps]),
        brands
      );
      brand = brand || find(brandHasApps, brands);
      const { brandId, stores } = brand || { stores: [], brandId: null };
      const appNames = appsForStores(stores);
      if (contains("swipely", appNames)) {
        return `/b/${brandId}/reports/sales_insights`;
      } else if (contains("breadcrumb", appNames)) {
        return getRedirect(brands, "s", storeIdForApp("breadcrumb", stores));
      } else if (contains("inventory", appNames)) {
        return getRedirect(brands, "s", storeIdForApp("inventory", stores));
      } else {
        return "/";
      }
    }
    case "s": {
      const stores = pluckEntityStores(brands);
      const storesWithApps = filter(storeHasApps, stores);
      let store;
      if (vendorLocation) {
        store = find(propEq("id", vendorLocation), storesWithApps);
      } else if (locationName) {
        store = find(propEq("name", locationName), storesWithApps);
      }
      store = store || head(storesWithApps) || { apps: [], id: null };
      const { apps, id } = store;
      const appNames = pluck("name", apps as IEntityApp[]);
      if (contains("breadcrumb", appNames)) {
        return `/s/${id}/dashboard`;
      } else if (contains("swipely", appNames)) {
        return `/s/${id}/reports/sales_insights`;
      } else if (contains("inventory", appNames)) {
        return `/s/${id}/inventory/dashboard`;
      } else {
        return "/";
      }
    }
    default: {
      return getRedirect(brands, "s");
    }
  }
};

const getRouterLinkUrl = (
  vendorLevel: string,
  vendorLocation: string,
  section: string,
  feature: string | undefined,
  tab: string | undefined
): string => {
  const appBase = getAppBase(vendorLevel, vendorLocation);
  const featurePath = findSecondaryNavItem(
    TEMPORARY_GLOBAL_NAV_ITEMS.current,
    section,
    feature,
    tab
  );
  return featurePath ? `${appBase}${featurePath.path}` : "";
};

export {
  excludeAppBaseFromPath,
  getAppBase,
  getRedirect,
  getRouterLinkUrl,
  passPresentURLParams,
  sameBaseDifferentPath,
  samePathDifferentBrand,
  samePathDifferentStore,
};

export const useRouterLinkUrl = (
  section: string,
  feature?: string,
  tab?: string
) => {
  const { vendorLevel, vendorLocation } = useFeatureRouteParams();
  return useMemo(
    () => getRouterLinkUrl(vendorLevel, vendorLocation, section, feature, tab),
    [vendorLevel, vendorLocation, section, feature, tab]
  );
};
